"use client";
import { useMemo, useCallback } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TwoColumnLayout from "@/components/Layout/TwoColumnLayout";
import { DateRange } from "@/components/DateRange";
import { Limit } from "@/components/Limit";
import { Button } from "@/components/ui/button";
import { CardTitle } from "@/components/ui/card";
import { fieldsData } from "../../constants";
import {
  exportKeywordResultsToJSON,
  exportKeywordResultsToCSV,
} from "../../utils";
import { KeywordPlatforms, KeywordFields, KeywordKeywords } from "../forms";
import { useKeywordContainer } from "../../hooks/useKeywordContainer";
import { useKeywordsStore } from "@/stores/keywordsStore";

export default function KeywordsContainer() {
  const {
    state,
    postsData,
    isLoading,
    showResults,
    hasLaunched,
    selectedPlatformFilter,
    resultsCount,
    isEmptyResults,
    apiEndpoint,
    responseJSON,
    selectedToken,
    effectiveToken,
    isLaunchDisabled,
    isApiTokenLoading,
    tokenSelectedPlatforms,
    availableTokens,
    tokensLoading,
    handleShowResults,
    handleLaunch,
    handleCopyCode,
    handleTokenSelect,
    setSelectedPlatformFilter,
    monitoringType,
  } = useKeywordContainer();

  const handleExportJSON = useCallback(() => {
    exportKeywordResultsToJSON(postsData, "keyword-based-results.json");
  }, [postsData]);

  const handleExportCSV = useCallback(() => {
    exportKeywordResultsToCSV(postsData, "keyword-based-results.csv");
  }, [postsData]);

  const formatPlatformName = useCallback((platform: string) => {
    const platformMap: Record<string, string> = {
      youtube: "YouTube",
      tiktok: "TikTok",
      instagram: "Instagram",
      twitter: "Twitter",
      facebook: "Facebook",
      linkedin: "LinkedIn",
    };
    return (
      platformMap[platform.toLowerCase()] ||
      platform.charAt(0).toUpperCase() + platform.slice(1)
    );
  }, []);

  const keywords = useKeywordsStore((state) => state.keywords);

  const mainContent = useMemo(
    () => (
      <div className="scrollbar-thin flex flex-col gap-5 overflow-x-hidden overflow-y-auto pr-3">
        <CardTitle className="text-[28px] leading-[36px] -tracking-wide text-neutral-950">
          Playground
        </CardTitle>
        <div
          className="scrollbar-thin flex flex-col gap-15"
          style={{
            transform: "translateZ(0)",
            scrollBehavior: "auto",
            overscrollBehavior: "contain",
          }}
        >
          <Limit
            limit={state.limit}
            setLimit={state.setLimit}
            useHeader
            customWidth="240px"
          />
          <DateRange
            dateRange={state.dateRange}
            setDateRange={(value) => state.setDateRange(value as any)}
            useHeader
            customWidth="240px"
          />
          <KeywordPlatforms
            selectedPlatforms={state.selectedPlatforms}
            setSelectedPlatforms={state.setSelectedPlatforms}
            enabledPlatforms={tokenSelectedPlatforms}
            isApiTokenLoading={isApiTokenLoading}
          />
          <KeywordFields
            platformsSelected={state.selectedPlatforms}
            data={fieldsData}
            title="Fields"
            description="Select the fields and subfields you would like to get articles from"
            selectedSubFields={state.selectedSubFields}
            setSelectedSubFields={state.setSelectedSubFields}
            storeName="keywordStore"
          />
          <KeywordKeywords onKeywordsChange={() => {}} />
        </div>
        <div className="flex flex-row items-center gap-3">
          <Button
            variant="default"
            icon
            onClick={handleLaunch}
            className="h-[48px] w-[240px] hover:scale-none"
            disabled={
              isLaunchDisabled ||
              state.selectedPlatforms.length === 0 ||
              state.selectedSubFields.length === 0
            }
          >
            {effectiveToken ? (
              <>
                <i className="ri-arrow-right-s-fill text-[18px]"></i>
                Launch
              </>
            ) : (
              "Missing API Token"
            )}
          </Button>
          {keywords.main.length === 0 &&
            keywords.additional.length === 0 &&
            keywords.excluded.length === 0 && (
              <p className="text-xs text-neutral-500">
                <i className="ri-information-line mr-1"></i>
                Launching without <span className="font-bold">
                  keywords
                </span>{" "}
                will return results based on the <span>fields</span> you have
                selected.
              </p>
            )}
        </div>
      </div>
    ),
    [
      state,
      handleLaunch,
      isLaunchDisabled,
      effectiveToken,
      tokenSelectedPlatforms,
    ],
  );

  // Memoize additional header content
  const additionalHeaderContent = useMemo(
    () =>
      state.selectedPlatforms.length > 1 ? (
        <Select
          value={selectedPlatformFilter}
          onValueChange={setSelectedPlatformFilter}
        >
          <SelectTrigger className="border-dark-border bg-dark-surface !w-[150px] border text-white !shadow-none">
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent className="!border-dark-border bg-dark-surface !w-[150px] !min-w-[150px] border text-white shadow-none">
            <SelectItem
              value="all"
              className="hover:bg-dark-elevated text-white"
            >
              All
            </SelectItem>
            {state.selectedPlatforms.map((platform) => (
              <SelectItem
                key={platform}
                value={platform}
                className="hover:bg-dark-elevated text-white"
              >
                {formatPlatformName(platform)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null,
    [
      state.selectedPlatforms,
      selectedPlatformFilter,
      setSelectedPlatformFilter,
      formatPlatformName,
    ],
  );

  // Memoize results description
  const resultsDescription = useMemo(
    () => (
      <span>
        Found{" "}
        <span className="font-bold">
          {(resultsCount as number).toLocaleString()} results
        </span>{" "}
        matching your filter.
      </span>
    ),
    [resultsCount],
  );

  return (
    <TwoColumnLayout
      data={postsData}
      isLoading={isLoading}
      isEmptyResults={isEmptyResults}
      responseJSON={responseJSON}
      apiEndpoint={apiEndpoint}
      apiToken={effectiveToken}
      isApiTokenLoading={isApiTokenLoading}
      resultsTitle="Results"
      resultsDescription={resultsDescription}
      showResults={showResults}
      onShowResults={handleShowResults}
      onLaunch={handleLaunch}
      onCopyCode={handleCopyCode}
      handleExportCSV={handleExportCSV}
      handleExportJSON={handleExportJSON}
      floatingButtonCondition={hasLaunched}
      leftPanelMinSize={50}
      rightPanelMinSize={30}
      additionalHeaderContent={additionalHeaderContent}
      showTokenDropdown={true}
      onTokenSelect={handleTokenSelect}
      monitoringType={monitoringType}
      selectedToken={selectedToken}
      availableTokens={availableTokens}
      tokensLoading={tokensLoading}
    >
      {mainContent}
    </TwoColumnLayout>
  );
}
